import {
  customerIsMixed,
  userHasMultipleCustomerNumbers,
  userIsActive,
  userIsIndividual,
  userIsUpcoming,
} from '@/helpers/customerUtils';
import { getCustomerType, trackingLogin } from '@/helpers/trackingHelper';
import { Account, Customer } from '@/types/Customer';
import { AuthUser, State } from '@/types/State';
import localforage from 'localforage';
import { get, includes } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageLoader from 'ui/components/PageLoader';
import {
  fetchAccount,
  fetchAccountCustomerId,
  fetchCustomer,
  fetchSelectedCustomerFacilities,
  setNonCustomer,
  setSelectedProperty,
} from '../../app/slices/userSlice';
import userManager from '../../app/userManager';
import Error from '../../components/error/Error';
import NoUserError from '../../components/error/NoUserError';

const Callback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const [hasNoUser, setHasNoUser] = useState(false);
  const auth = useSelector((state: State) => state?.auth);
  const shouldRedirectFornya = !!localStorage.getItem('fornya');
  const [confirmAccount, setConfirmAccount] = useState<string | false>(false);
  const [confirmAccountReady, setConfirmAccountReady] = useState(false);

  useEffect(() => {
    localforage.getItem('bekrafta_konto').then((code: any) => {
      if (code) {
        setConfirmAccount(code as string);
      }
      setConfirmAccountReady(true);
    });
  }, []);

  useEffect(() => {
    if (auth?.user !== null) {
      setHasError(false);
    }
    const tiemoutId = setTimeout(() => {
      setHasNoUser(true);
    }, 40000);

    return () => clearTimeout(tiemoutId);
  }, [auth]);

  const isCompanyNotAssigned = useCallback(
    (user: AuthUser) => {
      // Check if customer has role CompanyNotAssigned, redirect if true

      if (
        get(user, 'profile.role') &&
        user.profile.role.indexOf('CompanyNotAssigned') > -1 &&
        auth?.user &&
        get(auth, 'user.profile.amr') &&
        auth.user.profile.amr.indexOf('Company') > -1 &&
        !confirmAccount
      ) {
        navigate('/foretag/skapa-anvandare');
        return false;
      }

      return true;
    },
    [auth, confirmAccount, navigate],
  );

  // Forward the pending destination (stored by App.tsx before signinRedirect)
  // so linked-account users still land on the page they originally requested
  // once they've picked/confirmed an account on the switch-user page.
  const navigateToSwitchUser = useCallback(
    (params: string) => {
      const redirectUri = localStorage.getItem('redirectUri');
      localStorage.removeItem('redirectUri');
      const search = new URLSearchParams(params);
      if (redirectUri) {
        search.set('redirect', redirectUri);
      }
      const query = search.toString();
      navigate(`/switch-user${query ? `?${query}` : ''}`);
    },
    [navigate],
  );

  const redirectToFornya = useCallback((customer: Customer) => {
    const isCompany = !userIsIndividual(customer);
    const customerNumber = customer.customerNumber;
    const globalWindow = window as any;
    const token = globalWindow?.token;
    const redirectUrl = `${
      import.meta.env.VITE_APP_OLD_ME_URL
    }?customerNumber=${customerNumber}&token=${token}&returnurl=/${
      isCompany ? 'foretag/' : ''
    }fornya-elavtal`;
    localStorage.removeItem('fornya');
    window.location.href = redirectUrl;
  }, []);

  const successCallback = useCallback(
    (user: AuthUser) => {
      dispatch(fetchAccount() as any)
        .then((account: Account) => {
          if (!isCompanyNotAssigned(user)) {
            return;
          }

          if (includes(user.profile.role, 'NonCustomer')) {
            dispatch(setNonCustomer(false) as any).then(() => {
              if (confirmAccount) {
                navigate(`/bekrafta_konto/${confirmAccount}`);
              } else if (get(account, 'payload.hasLinkedAccounts')) {
                navigateToSwitchUser('');
              } else {
                trackingLogin('NonCustomer');
                navigate('/non-customer');
              }
            });
            return;
          }
          const customerId = get(user, 'profile.mecid') as any;

          if (!customerId) {
            navigate('/signout');
            return;
          }
          dispatch(fetchCustomer(customerId) as any)
            .then((customer: Customer) => {
              if (customer === undefined) {
                setHasNoUser(true);
                return;
              }

              try {
                dispatch(fetchSelectedCustomerFacilities() as any);
                trackingLogin(getCustomerType(customer), customer);
              } catch (error) {
                console.log('Tracking login', error);
              }
              if (!userIsActive(customer) && !userIsUpcoming(customer)) {
                dispatch(
                  setSelectedProperty({
                    infraServices: [],
                  }),
                );
                if (confirmAccount) {
                  navigate(`/bekrafta_konto/${confirmAccount}`);
                } else if (get(account, 'payload.hasLinkedAccounts')) {
                  navigateToSwitchUser('');
                } else {
                  navigate('/non-customer');
                }

                return;
              }

              if (
                ((customerIsMixed(customer) &&
                  userHasMultipleCustomerNumbers(customer)) ||
                  get(account, 'payload.hasLinkedAccounts')) &&
                !confirmAccount
              ) {
                navigateToSwitchUser('showModal=true&autoLogin=true');
                return;
              }

              if (shouldRedirectFornya) {
                redirectToFornya(customer);
                return;
              }

              dispatch(fetchAccountCustomerId(customerId) as any);

              if (localStorage.getItem('redirectUri')) {
                const redirectUri = localStorage.getItem('redirectUri');
                localStorage.removeItem('redirectUri');
                redirectUri && navigate(redirectUri);
                return;
              }

              if (confirmAccount) {
                navigate(`/bekrafta_konto/${confirmAccount}`);
              } else {
                navigate('/');
              }
            })
            .catch((error: Error) => {
              console.error('fetchCustomer', error, user);
              setHasError(true);
              throw error;
            });
        })
        .catch((error: Error) => {
          console.error('fetchAccount', error, user);
          setHasError(true);
          throw error;
        });
    },
    [
      dispatch,
      isCompanyNotAssigned,
      confirmAccount,
      navigate,
      navigateToSwitchUser,
      shouldRedirectFornya,
      redirectToFornya,
    ],
  );

  const errorCallback = useCallback(
    (error: Error) => {
      if (hasError || auth?.isLoadingUser) {
        return;
      }

      if (
        error?.message === 'No matching state found in storage' ||
        error?.message === 'No state in response'
      ) {
        userManager.clearStaleState().catch((stateError) => {
          console.error(
            'clearStaleState in callback error handler',
            stateError,
          );
        });
        navigate('/', { replace: true });
        return;
      }

      if (
        error?.message === 'Network Error' ||
        error?.message?.includes('invalid_grant')
      ) {
        setHasError(true);
        return;
      }

      console.error('errorCallback', error?.message);
      setHasError(true);
    },
    [auth?.isLoadingUser, hasError, navigate],
  );

  // Handle OIDC redirect callback - wait for confirmAccount to be resolved from localforage
  useEffect(() => {
    if (!confirmAccountReady) return;

    userManager
      .signinRedirectCallback()
      .then((user) => successCallback(user as unknown as AuthUser))
      .catch((error: Error) => errorCallback(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAccountReady]);

  return hasError ? (
    <Error />
  ) : hasNoUser ? (
    <NoUserError />
  ) : (
    <PageLoader title='Hämtar dina uppgifter...' />
  );
};

export default Callback;
